import { toRaw, type App } from 'vue'
import {
  MapManager,
  type MapManagerSettings,
  type StyleSpecification,
} from '@onxmaps/map-x'
import { createStylesheetProviderBase } from '@onxmaps/map-x/stylesheet'
import { captureException } from '@sentry/vue'

import subscriptionService from '@/services/subscription-service'
import {
  initialStylesheet,
  layerManagementReady,
} from '@/services/layer-management-service'

import { initialPitch } from '@/services/map-provider/map-config'
import { WEBMAP_MAP_MANAGER_KEY } from '@/services/map-provider/injection-key'
import { WebmapMapViewerMapbox } from '@/lib/map-x/webmap-map-viewer-mapbox'
import { mapXTransformRequest } from '@/lib/map-x/transform-request'

import {
  setMapManagerReady,
  setMapViewerReady,
  setMapManager,
} from '@/services/map-provider/map-manager-state'

import { setCamera } from '@/services/map-service'
import Geographic from '@/lib/geographic'
import { MAP_MAX_ZOOM, MAP_MIN_ZOOM } from '@/lib/enums'
import { error } from '@/lib/logger'
// eslint-disable-next-line no-restricted-imports
import { getInitView } from '@/lib/map-viewer/viewer-camera'
// eslint-disable-next-line no-restricted-imports
import { setLayerOrder } from '@/lib/map-viewer/viewer-layers'

/**
 * Creates a MapManager, mimics MapProviderBefore.vue `@onxmaps/map-x-vue` behavior & provides it app-wide
 * @param app - Vue application instance
 * @param key - Optional symbol key for multi-map support (defaults to WEBMAP_MAP_MANAGER_KEY)
 * @param customOptions - Optional partial MapManagerSettings to override defaults
 * @returns Promise resolving to MapManager instance or null on error
 */
export async function provideMapManager(
  app: App,
  key: symbol = WEBMAP_MAP_MANAGER_KEY,
  customOptions?: Partial<MapManagerSettings>
) {
  // Wait for layer management to be ready - ensure initialStylesheet is available
  await layerManagementReady()

  // Wait for subscriptions to be ready - ensure subscription service state is correct
  await subscriptionService.subscriptionsReadyPromise

  const stylesheet = toRaw(initialStylesheet())

  if (!stylesheet) {
    const errorMessage = 'No stylesheet available after layerManagementReady()'
    error('Map', errorMessage)
    captureException(new Error(errorMessage), { tags: { map: 'map-x' } })
    return
  }

  // create initial view mapbox object
  const view = getInitView(initialPitch.value)
  const { latitude, longitude, height } = view.location
  const mapboxView = {
    zoom: height,
    center: [longitude, latitude],
    ...view,
  }

  const visibleCenter = new Geographic(longitude, latitude, height)
  const camera = view.withVisibleCenter(visibleCenter)
  setCamera(camera)
  setLayerOrder(stylesheet.layers)

  /**
   * MapManager implements a StylesheetProvider.
   *
   * MapManager sets the viewer on the StylesheetProvider and enables terrain by default.
   * ```typescript
   *   this.stylesheet = stylesheet;
   *   this.stylesheet.setViewer(this.viewer);
   *   this.stylesheet.setTerrainEnabled(true);
   * ```
   *
   * The MapManager news up the EventsManager with the StylesheetProvider,
   * 'map-x-style-updated' is emitted on stylesheet changes.
   * This is in addition to viewer stylesheet events.
   * ```
   *   stylesheet.setOnChange((e) => this.emit('map-x-style-updated', e));
   * ```
   *
   * @see {@link https://github.com/onXmaps/map-x/blob/main/src/stylesheet/createStylesheetProviderBase.ts}
   * @see {@link https://github.com/onXmaps/map-x/blob/main/src/events/EventsManager.ts}
   * @see {@link https://github.com/onXmaps/map-x/blob/main/src/MapManager.ts}
   */
  const stylesheetProvider = createStylesheetProviderBase({
    style: stylesheet as StyleSpecification,
  })

  const options: MapManagerSettings = {
    stylesheet: stylesheetProvider,
    viewer: WebmapMapViewerMapbox,
    transformRequest: mapXTransformRequest,
    center: [mapboxView.center[0], mapboxView.center[1]],
    zoom: mapboxView.zoom,
    pitch: initialPitch.value || mapboxView.pitch,
    bearing: mapboxView.bearing,
    minZoom: MAP_MIN_ZOOM,
    maxZoom: MAP_MAX_ZOOM,
    preserveDrawingBuffer: true,
    interactive: true,
    isAutoSelectFeatureOnClickEnabled: false,
    // Spread custom options to override defaults
    ...customOptions,
  }

  try {
    const mapManager = new MapManager(options)

    // Store instance for direct access (supports multi-map via optional key parameter)
    setMapManager(mapManager, key)

    app.provide(key, mapManager)

    // MapManager instance is created and ready to use (ready for makeReady() to be called)
    setMapManagerReady(true, key)

    // Listen for the map-x-ready event to know when makeReady() has completed
    mapManager.events.once('map-x-ready', () => {
      setMapViewerReady(true, key)
    })

    return mapManager
  } catch (e) {
    const errorMessage = 'Failed to create MapManager'
    error('Map', errorMessage, e)
    // eslint-disable-next-line no-console
    console.error('[fish-web] MapManager constructor threw:', e)
    captureException(e instanceof Error ? e : new Error(errorMessage), {
      tags: { map: 'map-x' },
    })
    return null
  }
}
